import * as React from 'react'
import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import BottomShade from 'components/BottomShade'
import Button from 'components/Button'
import { Portal } from 'components/Portal'
import Form from 'components/fields/Form'
import FormRow from 'components/fields/FormRow'
import Radio, { RadioLabel } from 'components/fields/Radio'
import { ActionButtonsPortalContext } from 'layouts/DefaultLayout'
import { ensureProtocol } from 'lib/helpers'

const StyledBottomShade = styled(BottomShade)`
	bottom: 80px;
`

const StyledForm = styled(Form)`
	gap: ${({ theme }) => theme.spaces.space0};
`

const WebsiteItemOuter = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	gap: ${({ theme }) => theme.spaces.space4};
	${({ theme }) => theme.typography.caption};
	padding: ${({ theme }) => theme.spaces.space5};
	background-color: ${({ theme }) => theme.colors.light.grayscale[15]};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};

	${FormRow} {
		width: 100%;
		margin-bottom: 0;
		gap: ${({ theme }) => theme.spaces.space4};
	}

	${RadioLabel} {
		flex: 1;
		display: flex;
		${({ theme }) => theme.typography.caption};
	}
`

const Website = styled.span`
	display: inline-block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	width: 250px;
`

const WebsitesColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space4};
`

type Props = {
	domains: string[]
	onSubmit: (website: string) => void
}

const WebsiteListForm = ({ domains, onSubmit }: Props) => {
	const { register, handleSubmit } = useForm({
		defaultValues: {
			website: ensureProtocol(domains[0]),
		},
	})
	const formId = React.useId()

	const _onSubmit: Parameters<typeof handleSubmit>['0'] = async ({ website }) => {
		onSubmit(website)
	}

	return (
		<StyledForm id={formId} onSubmit={handleSubmit(_onSubmit)}>
			<WebsitesColumn>
				{domains.map((domain) => {
					const validUrl = ensureProtocol(domain)
					return (
						<WebsiteItemOuter key={domain}>
							<Radio id={domain} value={validUrl} {...register('website')} label={<Website>{validUrl}</Website>} />
						</WebsiteItemOuter>
					)
				})}
			</WebsitesColumn>
			<Portal context={ActionButtonsPortalContext}>
				<StyledBottomShade />
				<Button fullWidth form={formId} kind="primary" type="submit" disableHover>
					Set up
				</Button>
			</Portal>
		</StyledForm>
	)
}

export default WebsiteListForm
